import { useQuery } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_FACTORY_ABI } from '../abis/ChallengeFactoryABI'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import { formatAddress, CHALLENGE_STATE, type ChallengeListItem } from '../types/challenge'

// A challenge that can never run: already refunded, or its start time passed without
// reaching the minimum participants (so activating it would only refund). These are
// terminal/dead and hidden from the main listing.
function isFailedToLaunch(item: ChallengeListItem): boolean {
  const now = Math.floor(Date.now() / 1000)
  return (
    item.state === CHALLENGE_STATE.REFUNDED ||
    (item.state === CHALLENGE_STATE.PENDING &&
      now >= item.startTime &&
      item.participantCount < item.minParticipants)
  )
}

// Reads the trust-critical on-chain state for one challenge (money/rules/membership).
// Display metadata (name/emoji/color) is merged separately from off-chain storage.
async function fetchOneChallengeData(
  address: string,
  provider: ethers.JsonRpcProvider,
): Promise<ChallengeListItem> {
  const contract = new ethers.Contract(address, CHALLENGE_ABI, provider)
  const [
    state,
    creator,
    challengeType,
    startTime,
    endTime,
    entryFee,
    winnerCount,
    minParticipants,
    maxParticipants,
    participantCount,
  ] = await Promise.all([
    contract.state() as Promise<bigint>,
    contract.creator() as Promise<string>,
    contract.challengeType() as Promise<bigint>,
    contract.startTime() as Promise<bigint>,
    contract.endTime() as Promise<bigint>,
    contract.entryFee() as Promise<bigint>,
    contract.winnerCount() as Promise<bigint>,
    contract.minParticipants() as Promise<bigint>,
    contract.maxParticipants() as Promise<bigint>,
    contract.participantCount() as Promise<bigint>,
  ])

  return {
    address,
    name: '', // display name comes from off-chain storage (merged below)
    creator: creator.toLowerCase(),
    challengeType: Number(challengeType),
    state: Number(state),
    startTime: Number(startTime),
    endTime: Number(endTime),
    entryFee: BigInt(entryFee.toString()),
    winnerCount: Number(winnerCount),
    participantCount: Number(participantCount),
    maxParticipants: Number(maxParticipants),
    minParticipants: Number(minParticipants),
  }
}

export async function fetchChallenges(): Promise<ChallengeListItem[]> {
  const factoryAddress = import.meta.env.VITE_FACTORY_ADDRESS
  if (!factoryAddress) return []

  const provider = getReadProvider()
  const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, provider)

  // The factory tracks every clone it created — one call, no event-log scanning.
  const addresses = (await (factory.getChallenges as () => Promise<string[]>)()).map((a) =>
    a.toLowerCase(),
  )

  const items = await Promise.all(addresses.map((a) => fetchOneChallengeData(a, provider)))

  // Merge off-chain display metadata: name (title), cosmetic theme (emoji/color), and the
  // admin 'hidden' flag. Best-effort and parallel.
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  const hiddenAddresses = new Set<string>()
  await Promise.all(
    items.map(async (item) => {
      try {
        const resp = await fetch(`${apiBase}/api/challenges/${item.address}`)
        if (!resp.ok) return
        const data = (await resp.json()) as {
          title?: string
          emoji?: string
          color?: string
          hidden?: boolean
        }
        if (data.title) item.name = data.title
        item.emoji = data.emoji ?? null
        item.color = data.color ?? null
        if (data.hidden) hiddenAddresses.add(item.address)
      } catch {
        // ignore — fall back to defaults below
      }
    }),
  )

  // Fallback name for challenges with no off-chain metadata yet.
  for (const item of items) {
    if (!item.name) item.name = formatAddress(item.address)
  }

  // Hide admin-hidden challenges and failed-to-launch (refunded / start passed under min).
  return items.filter((item) => !hiddenAddresses.has(item.address) && !isFailedToLaunch(item))
}

export function useChallenges() {
  return useQuery<ChallengeListItem[]>({
    queryKey: ['challenges'],
    queryFn: fetchChallenges,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
